import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react/lib/Spinner";

import SavedSearch from "./saved-search/SavedSearch";
import Listing from "./listing/Listing";
import { attributeApiCall, getFileApiCall } from "./redux/apiCalls";
import { startFetchingAttributesAttachFromIci } from "./redux/slice";
import { hideNotification, showNotificationSuccess } from "../add-to-ici/redux/slice";
import { transformAttributeData, updateAttributeData, updateSetDocsForAttachToEmailData } from "./utils";
import { TEXT } from "../../shared/constants/texts";
import { logError } from "../../shared/services/errorLogging";

const AttachFromIci: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.attachFromIciSlice);
  const { isFetchingSavedSearchData, docsToAttach } = state;
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    localStorage.setItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY, JSON.stringify(errorValidationMsg));
    getAttributes();
  }, []);

  const getAttributes = async () => {
    dispatch(startFetchingAttributesAttachFromIci());
    const attributeResponse = await attributeApiCall({
      ...apiCallParams,
      retryFunctionCall: () => getAttributes(),
    });
    const { response, error } = attributeResponse;
    if (response) {
      const attributeData = transformAttributeData(response);
      updateAttributeData(attributeData);
    }
    if (error) {
      updateAttributeData([]);
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|attach-from-ici|index - getAttributes",
          callerApi: "api/instance/FindProjectionsKeyValuePair",
        },
      });
    }
  };

  const attachDocumentsToEmail = () => {
    const totalCount = docsToAttach.length;
    docsToAttach.forEach(async (doc, index) => {
      let fileResponse = null;
      let fileName = "";
      let fileExtension = "";
      if (doc.sysId) {
        fileName = doc.name;
        fileExtension = getExtension(getFileName(doc.filePath));
        fileResponse = await getFileApiCall({
          ...apiCallParams,
          requestData: {
            agreementId: doc.sysId,
            contractType: doc.entityName,
            extension: fileExtension,
          },
          retryFunctionCall: () => attachDocumentsToEmail(),
        });
      } else if (doc.SysId) {
        fileName = doc.Name;
        fileExtension = getExtension(getFileName(doc.FilePath));
        fileResponse = await getFileApiCall({
          ...apiCallParams,
          requestData: {
            agreementId: doc.SysId,
            contractType: doc.ICMContractTypeName,
            extension: fileExtension,
          },
          retryFunctionCall: () => attachDocumentsToEmail(),
        });
      }
      const { response, error } = fileResponse;
      if (response) {
        let transformedResponse = response;
        if ("$value" in transformedResponse) {
          transformedResponse = transformedResponse["$value"];
        }
        Office.context.mailbox.item.addFileAttachmentFromBase64Async(
          transformedResponse,
          fileName + "." + fileExtension,
          (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
              console.log("error attaching document to email", asyncResult);
            }
          }
        );
      }
      if (error) {
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction: "ClientApp|src|taskpane|containers|attach-from-ici|index - attachDocumentsToEmail",
            callerApi: "Agreement/GetFile?fileType=&agreementId=&contractType=",
          },
        });
      }
      if (index === totalCount - 1) {
        const notificationMsg = `${totalCount} ${TEXT.DOCUMENT_ATTACHED}`;
        dispatch(showNotificationSuccess(notificationMsg));
        updateSetDocsForAttachToEmailData([]);
        setTimeout(() => {
          dispatch(hideNotification());
        }, 5000);
      }
    });
  };

  const getFileName = (path) => {
    const fileName = path.split("\\");
    return fileName[fileName.length - 1];
  };

  const getExtension = (fileName) => {
    if (fileName) {
      const extension = fileName.split(".");
      return extension[extension.length - 1];
    }
  };

  console.log("state in attach from ici (index):", state);

  return (
    <div className="attach-from-ici">
      <SavedSearch />
      {isFetchingSavedSearchData ? (
        <div className="saved-search-loading-container-attach-from-ici">
          <Spinner size={3} label={TEXT.LOADING_SAVED_SEARCHES} />
        </div>
      ) : (
        <Listing />
      )}
      <div className="button-container-attach-from-ici">
        <button disabled={!(docsToAttach.length > 0)} onClick={attachDocumentsToEmail}>
          {TEXT.ATTACH_DOCUMENT_TO_EMAIL}
        </button>
      </div>
    </div>
  );
};

export default AttachFromIci;
